import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatHeat, getCategoryName } from "@/utils/content";

interface ArticleCardProps {
  article: Article;
  variant?: "default" | "compact" | "wide";
  index?: number;
}

export default function ArticleCard({ article, variant = "default", index }: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/topics/${article.slug}`}
        className="group flex cursor-pointer items-start gap-4 border-b border-background-200 py-4 last:border-b-0"
      >
        <span className="flex h-14 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 五一假期事件盘点`}
            className="h-full w-full object-cover object-top"
          />
        </span>
        <span className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-primary-600">{getCategoryName(article.category)}</span>
          <span className="line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
            {article.title}
          </span>
          <span className="flex items-center gap-2 text-xs text-foreground-400">
            <span>{article.dateText}</span>
            <span className="h-1 w-1 rounded-full bg-background-400" />
            <span>{article.readTime}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={`/topics/${article.slug}`}
        className="group grid cursor-pointer grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 sm:grid-cols-2"
      >
        <div className="h-52 w-full overflow-hidden bg-background-200 sm:h-full sm:min-h-[210px]">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 五一假期事件盘点`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
        </div>
        <div className="flex flex-col justify-center gap-3 p-5 md:p-6">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary-100 px-2.5 py-1 text-xs font-medium text-primary-700">
            {getCategoryName(article.category)}
          </span>
          <h3 className="font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
            {article.title}
          </h3>
          <p className="line-clamp-3 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line" />
              {article.dateText}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line" />
              {article.readTime}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line" />
              {formatHeat(article.hot)} 热度
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/topics/${article.slug}`} className="flex h-full cursor-pointer flex-col">
        <div className="relative h-48 w-full overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 五一假期事件盘点`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
          />
          {typeof index === "number" && index < 3 && (
            <span className="absolute left-3 top-3 flex h-7 w-7 items-center justify-center rounded-md bg-primary-600 font-heading text-sm font-bold text-background-50">
              {index + 1}
            </span>
          )}
          <span className="absolute right-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-xs font-medium text-primary-700 backdrop-blur">
            {getCategoryName(article.category)}
          </span>
        </div>
        <div className="flex flex-1 flex-col gap-3 p-5">
          <h3 className="line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
            {article.title}
          </h3>
          <p className="line-clamp-2 text-sm leading-relaxed text-foreground-600">{article.excerpt}</p>
          <div className="mt-auto flex flex-wrap items-center gap-3 pt-1 text-xs text-foreground-400">
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line" />
              {article.dateText}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-fire-line" />
              {formatHeat(article.hot)}
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}